import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Package, TrendingUp, Leaf, Droplet, Recycle, Target } from 'lucide-react';

interface EnhancedMetricsOverviewProps {
  overview: {
    totalProducts: number;
    saferIngredientsPercentage: number;
    carbonReduced: number;
    waterSaved: number;
    wasteReduced: number;
  };
  loading: boolean;
}

export const EnhancedMetricsOverview = ({ overview, loading }: EnhancedMetricsOverviewProps) => {
  const metrics = [
    {
      title: 'Total Products',
      value: loading ? '...' : overview.totalProducts.toString(),
      subtitle: 'Products in portfolio',
      icon: Package,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Safer Ingredients',
      value: loading ? '...' : `${overview.saferIngredientsPercentage}%`,
      subtitle: 'Of total ingredients',
      icon: Leaf,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      progress: overview.saferIngredientsPercentage,
      target: 85,
    },
    {
      title: 'Carbon Reduced',
      value: loading ? '...' : `${overview.carbonReduced.toLocaleString()} kg`,
      subtitle: 'CO₂ equivalent',
      icon: TrendingUp,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
    },
    {
      title: 'Water Saved',
      value: loading ? '...' : `${overview.waterSaved.toLocaleString()} L`,
      subtitle: 'Water consumption reduced',
      icon: Droplet,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-100',
    },
    {
      title: 'Waste Reduced',
      value: loading ? '...' : `${overview.wasteReduced.toLocaleString()} kg`,
      subtitle: 'Total waste reduction',
      icon: Recycle,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      {metrics.map((metric) => {
        const Icon = metric.icon;
        const isProgressMetric = typeof metric.progress === 'number';
        
        return (
          <Card key={metric.title} className="relative overflow-hidden hover:shadow-md transition-shadow animate-fade-in">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
              <div className={`p-2 rounded-full ${metric.bgColor}`}>
                <Icon className={`h-4 w-4 ${metric.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <p className="text-xs text-muted-foreground mb-2">
                {metric.subtitle}
              </p>
              
              {isProgressMetric && metric.target && (
                <div className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span>Progress</span>
                    <span className="flex items-center gap-1">
                      <Target className="h-3 w-3" />
                      {metric.target}%
                    </span>
                  </div>
                  <Progress 
                    value={metric.progress} 
                    className="h-2"
                  />
                  <div className="text-xs text-muted-foreground">
                    {metric.progress >= metric.target ? (
                      <span className="text-green-600 font-medium">Target achieved! 🎯</span>
                    ) : (
                      <span>{(metric.target - metric.progress).toFixed(1)}% to target</span>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};